import { type ReactNode, useCallback, useMemo } from "react";
import { useLocation, useParams } from "wouter";

import { ConnectorConnectAction } from "@/components/connectors/ConnectorConnectAction.tsx";
import { ConnectorLogo } from "@/components/connectors/ConnectorLogo.tsx";
import { ConnectorRow } from "@/components/connectors/ConnectorTile.tsx";
import { getConnectorIconForVariant } from "@/components/connectors/providerIcons.tsx";
import { useConnectorCatalog } from "@/components/connectors/useConnectorCatalog.ts";
import { Loader } from "@/components/loader/Loader.tsx";
import { usePackMutations, usePacks } from "@/components/packs/usePacks.ts";
import {
  AreaPills,
  PACKS_AREA_ROLE_STORAGE_KEY,
  useAreaRoleFilter,
} from "@/components/routines/areaRoleFilter.tsx";
import { useRoutinesV2 } from "@/components/routines/useRoutinesV2.ts";
import {
  cardListClass,
  cardSurfaceClass,
  clickableCardClass,
  galleryCardsClass,
} from "@/components/threads/shared.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { Button } from "@/components/ui/button.tsx";
import { useRoutines } from "@/hooks/useRoutines.ts";
import { useSkills } from "@/hooks/useSkills.ts";
import { cn } from "@/lib/utils.ts";
import {
  appliedFilterLabel,
  type PanelControlAxes,
  PanelControls,
} from "@/views/Settings/panelControls.tsx";
import {
  panelDescriptionClass,
  SettingsPanelHeader,
} from "@/views/Settings/SettingsPanelHeader.tsx";
import { ALL_AREAS } from "@/shared/areaRoleFilter.ts";
import { connectorConnectMode } from "@/shared/connectors/connectMode.ts";
import { packsForSelection } from "@/shared/packList.ts";
import type { ConnectorCatalogItem } from "@/types/connectors.ts";
import type { PackResp } from "@/types/packs.ts";

const PACKS_BASE_PATH = "/settings/packs";

/** The routines and skills this user can see, by system routine id and skill name. */
type VisibleItems = { routineIds: ReadonlySet<string>; skillNames: ReadonlySet<string> };

/**
 * What a pack holds, in one line: "2 routines · 3 skills" for an area pack.
 * Only what the user can see is counted — a cohort-only routine is in a pack
 * for its cohort — so the card agrees with the detail.
 */
const packContentsLine = (
  pack: PackResp,
  packs: readonly PackResp[],
  visible: VisibleItems,
): string => {
  if (pack.kind === "role") {
    return (pack.packs ?? []).map((id) => packs.find((p) => p.id === id)?.name ?? id).join(" · ");
  }
  const count = (n: number, one: string) => (n > 0 ? [`${n} ${one}${n === 1 ? "" : "s"}`] : []);
  return [
    ...count((pack.routines ?? []).filter((id) => visible.routineIds.has(id)).length, "routine"),
    ...count((pack.skills ?? []).filter((name) => visible.skillNames.has(name)).length, "skill"),
    ...count(pack.connectors?.length ?? 0, "connector"),
  ].join(" · ");
};

/**
 * Install, Remove, or "Installed with <role pack>" for an area pack that a
 * role pack brought: a user removes a full pack only, so that one has no
 * Remove of its own.
 */
const PackInstallControl = ({ pack, packs }: { pack: PackResp; packs: readonly PackResp[] }) => {
  const { installPack, removePack, pendingPackId } = usePackMutations();
  const pending = pendingPackId === pack.id;
  const installedWith = pack.installedWith ?? [];

  if (installedWith.length > 0) {
    const names = installedWith.map((id) => packs.find((p) => p.id === id)?.name ?? id);
    return (
      <Badge variant="brand" size="sm">
        Installed with {names.join(" and ")}
      </Badge>
    );
  }
  // Stop propagation so the button doesn't bubble into the card's navigation.
  return pack.installed ? (
    <Button
      variant="outline"
      size="xs"
      disabled={pending}
      onClick={(e) => {
        e.stopPropagation();
        removePack(pack.id);
      }}
      data-statsig-name="packs-remove"
    >
      Remove
    </Button>
  ) : (
    <Button
      variant="primary"
      size="xs"
      disabled={pending}
      onClick={(e) => {
        e.stopPropagation();
        installPack(pack.id);
      }}
      data-statsig-name="packs-install"
    >
      Install
    </Button>
  );
};

/** A pack's card in the list: its name, description, contents and install control. Opens the pack's detail. */
const PackCard = ({
  pack,
  packs,
  visible,
}: {
  pack: PackResp;
  packs: readonly PackResp[];
  visible: VisibleItems;
}) => {
  const [, setLocation] = useLocation();
  const name = pack.name ?? pack.id;
  const handleOpen = () => setLocation(`${PACKS_BASE_PATH}/${encodeURIComponent(pack.id)}`);
  return (
    <article
      aria-label={name}
      tabIndex={0}
      className={cn(clickableCardClass, "flex flex-col")}
      onClick={handleOpen}
      onKeyDown={(e) => {
        // The card's own keys only: Enter on its Install button is the button's.
        if (e.target !== e.currentTarget || (e.key !== "Enter" && e.key !== " ")) return;
        e.preventDefault();
        handleOpen();
      }}
    >
      <div className="flex items-center gap-2">
        <span className="flex-1 min-w-0 font-medium text-sm truncate">{name}</span>
        {pack.installed && !pack.installedWith?.length && (
          <Badge variant="tertiary" size="sm">
            Installed
          </Badge>
        )}
      </div>
      {pack.description && (
        <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{pack.description}</div>
      )}
      {/* mt-auto pins the footer to the card floor, so a row of cards keeps
          its footers on one line regardless of description length. */}
      <div className="mt-auto flex items-center gap-2 pt-3">
        <span className="flex-1 min-w-0 text-xs text-muted-foreground truncate">
          {packContentsLine(pack, packs, visible)}
        </span>
        <PackInstallControl pack={pack} packs={packs} />
      </div>
    </article>
  );
};

/** One titled group of pack cards (role packs or area packs); nothing when it is empty. */
const PackSection = ({
  title,
  packs,
  allPacks,
  visible,
}: {
  title: string;
  packs: readonly PackResp[];
  allPacks: readonly PackResp[];
  visible: VisibleItems;
}) => {
  const headingId = `packs-section-${title.toLowerCase().replace(/\s+/g, "-")}`;
  if (packs.length === 0) return null;
  return (
    <section aria-labelledby={headingId} className="mb-6">
      <h3 id={headingId} className="mb-2 text-sm font-semibold">
        {title}
      </h3>
      <div className={galleryCardsClass}>
        {packs.map((pack) => (
          <PackCard key={pack.id} pack={pack} packs={allPacks} visible={visible} />
        ))}
      </div>
    </section>
  );
};

/**
 * The three reads the Packs page draws from: the packs, and the routines and
 * skills that name what they hold. A pack drawn without the other two would
 * look emptier than it is, so the page waits for all three and fails as one.
 */
const usePackPageReads = () => {
  const routinesV2 = useRoutinesV2();
  const packsRead = usePacks(routinesV2.enabled);
  const routinesRead = useRoutines();
  const skillsRead = useSkills();
  const failed = [
    packsRead.isError && packsRead.refetch,
    routinesRead.error && routinesRead.refetch,
    skillsRead.error && skillsRead.refetch,
  ].filter((refetch) => typeof refetch === "function");
  const handleRetry = async () => {
    await Promise.all(failed.map((refetch) => refetch()));
  };
  return {
    routinesV2,
    packs: packsRead.packs ?? [],
    routines: routinesRead.routines,
    skills: skillsRead.skills,
    isLoading: packsRead.isLoading || routinesRead.isLoading || skillsRead.isLoading,
    isError: failed.length > 0,
    handleRetry,
  };
};

/** What the page shows while its reads load, or when one has failed. */
const PackReadState = ({ isError, onRetry }: { isError: boolean; onRetry: () => void }) =>
  isError ? (
    <div className="py-12 text-center text-sm text-muted-foreground">
      <p>Couldn't load packs.</p>
      <Button
        variant="outline"
        size="sm"
        className="mt-3"
        onClick={onRetry}
        data-statsig-name="packs-load-retry"
      >
        Try again
      </Button>
    </div>
  ) : (
    <div className="flex items-center justify-center h-64">
      <Loader />
    </div>
  );

/** The packs list: the header, the area pills and Role filter, and the two sections. */
const PacksList = () => {
  const { routinesV2, packs, routines, skills, isLoading, isError, handleRetry } =
    usePackPageReads();
  const areaRole = useAreaRoleFilter({
    storageKey: PACKS_AREA_ROLE_STORAGE_KEY,
    enabled: routinesV2.enabled,
    statsigName: "packs-role",
  });
  const visible = useMemo<VisibleItems>(
    () => ({
      routineIds: new Set(routines.flatMap((r) => (r.systemRoutineId ? [r.systemRoutineId] : []))),
      skillNames: new Set(skills.map((s) => s.name)),
    }),
    [routines, skills],
  );
  const list = packsForSelection(packs, areaRole?.selection);
  const isNarrowed =
    !!areaRole && (areaRole.selection.area !== ALL_AREAS || areaRole.selection.roles.length > 0);
  const controls: PanelControlAxes = areaRole
    ? { filters: [areaRole.roleAxis], countApplied: true }
    : { filters: [] };

  return (
    <div className="pb-12">
      <SettingsPanelHeader
        title="Packs"
        filters={<PanelControls labeled {...controls} subject="packs" statsigName="packs-filter" />}
        mobileFilters={<PanelControls {...controls} subject="packs" statsigName="packs-filter" />}
        toolsKey={appliedFilterLabel(controls)}
      />
      <p className={panelDescriptionClass}>
        Sets of routines, skills and connectors for a kind of work. Install a pack to add its
        routines to your Routines page, then turn on the ones you want.
      </p>
      {isLoading || isError ? (
        <PackReadState isError={isError} onRetry={handleRetry} />
      ) : (
        <>
          {areaRole && (
            <AreaPills items={list.areaPacksForPills} filter={areaRole} statsigName="packs-area" />
          )}
          <PackSection
            title="Role packs"
            packs={list.rolePacks}
            allPacks={packs}
            visible={visible}
          />
          <PackSection
            title="Area packs"
            packs={list.areaPacks}
            allPacks={packs}
            visible={visible}
          />
          {list.rolePacks.length === 0 && list.areaPacks.length === 0 && (
            <div className="py-12 text-center text-sm text-muted-foreground">
              {isNarrowed
                ? 'No packs match. Pick "All" above, or change the roles under Filter.'
                : "No packs to show."}
            </div>
          )}
        </>
      )}
    </div>
  );
};

/** A connector's mark: the drawn brand mark when we ship one, else its favicon. */
const connectorIcon = (item: ConnectorCatalogItem) =>
  (item.provider && getConnectorIconForVariant(item.provider, item.iconVariant ?? "brand")) ?? (
    <ConnectorLogo websiteUrl={item.websiteUrl} name={item.name} size="sm" className="shrink-0" />
  );

/** A titled list of rows in a pack's detail. */
const DetailSection = ({ title, children }: { title: string; children: ReactNode }) => {
  const headingId = `pack-detail-${title.toLowerCase().replace(/\s+/g, "-")}`;
  return (
    <section aria-labelledby={headingId} className="mb-6">
      <h3 id={headingId} className="mb-2 text-sm font-semibold">
        {title}
      </h3>
      <div className={cardListClass}>{children}</div>
    </section>
  );
};

/** A routine or skill in a pack's detail, by the name the user knows it by. */
const DetailRow = ({ name, description }: { name: string; description?: string }) => (
  <div className={cardSurfaceClass}>
    <div className="text-sm font-medium">{name}</div>
    {description && <div className="mt-0.5 text-xs text-muted-foreground">{description}</div>}
  </div>
);

/** One pack's detail: what it holds, its connectors, and its install control. */
const PackDetail = ({ packId }: { packId: string }) => {
  const [, setLocation] = useLocation();
  const { packs, routines, skills, isLoading, isError, handleRetry } = usePackPageReads();
  const { data: catalog } = useConnectorCatalog();
  const pack = packs.find((p) => p.id === packId);
  const handleBack = useCallback(() => setLocation(PACKS_BASE_PATH), [setLocation]);

  // A pack names routines and skills by id; the rows show what the user knows
  // them by. One the user cannot see (a cohort-only routine) is left out.
  const packRoutines = useMemo(
    () => (pack?.routines ?? []).flatMap((id) => routines.filter((r) => r.systemRoutineId === id)),
    [pack, routines],
  );
  const packSkills = useMemo(
    () => (pack?.skills ?? []).flatMap((name) => skills.filter((s) => s.name === name)),
    [pack, skills],
  );
  const packConnectors = useMemo(
    () =>
      (pack?.connectors ?? []).flatMap((c) => {
        const item = catalog?.items.find((i) => i.id === c.id);
        return item ? [{ ...c, item }] : [];
      }),
    [pack, catalog],
  );

  if (isLoading || isError) return <PackReadState isError={isError} onRetry={handleRetry} />;

  return (
    <div className="pb-12">
      <SettingsPanelHeader
        title={pack?.name ?? "Pack"}
        onBack={handleBack}
        parentLabel="Packs"
        backStatsigName="packs-panel-back"
      />
      {!pack ? (
        <p className={panelDescriptionClass}>This pack is not available.</p>
      ) : (
        <>
          <div className="mb-6 flex items-start gap-3">
            <p className="flex-1 text-sm text-muted-foreground">
              {pack.description}{" "}
              {pack.kind === "area"
                ? "Install makes its routines available. Turn on the ones you want on the Routines page."
                : "Install adds each of its area packs."}
            </p>
            <PackInstallControl pack={pack} packs={packs} />
          </div>
          {pack.kind === "role" ? (
            <DetailSection title="Area packs">
              {(pack.packs ?? []).map((id) => {
                const areaPack = packs.find((p) => p.id === id);
                return (
                  <DetailRow
                    key={id}
                    name={areaPack?.name ?? id}
                    description={areaPack?.description}
                  />
                );
              })}
            </DetailSection>
          ) : (
            <>
              {packRoutines.length > 0 && (
                <DetailSection title="Routines">
                  {packRoutines.map((r) => (
                    <DetailRow key={r.id} name={r.name} description={r.description} />
                  ))}
                </DetailSection>
              )}
              {packSkills.length > 0 && (
                <DetailSection title="Skills">
                  {packSkills.map((s) => (
                    <DetailRow key={s.name} name={s.name} description={s.description} />
                  ))}
                </DetailSection>
              )}
              {packConnectors.length > 0 && (
                <DetailSection title="Connectors">
                  {packConnectors.map(({ id, use, gain, item }) => (
                    <ConnectorRow
                      key={id}
                      item={{
                        name: item.name,
                        description: use === "needs" ? `Needed. ${gain ?? ""}`.trim() : gain,
                        websiteUrl: item.websiteUrl,
                        icon: connectorIcon(item),
                        connected: item.connected,
                        needsReconnect: connectorConnectMode(item).needsReconnect,
                        action: <ConnectorConnectAction item={item} statsigName="packs-connect" />,
                      }}
                    />
                  ))}
                </DetailSection>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

/** The Packs tab of the settings screen: the list, or one pack's detail. */
export const PacksFlow = () => {
  const params = useParams<{ subId?: string }>();
  if (params.subId) return <PackDetail packId={decodeURIComponent(params.subId)} />;
  return <PacksList />;
};
